import type { Entity, Layer, Pan, SelectionMode } from "../state/types.js";
import {
  distanceToSegment,
  pointInPolygon,
  screenToWorld,
  segmentsIntersect,
} from "../utils/geometry.js";

export interface HitTestInput {
  entities: Entity[];
  layers: Record<string, Layer>;
  layerOrder: string[];
  zoom: number;
  pan: Pan;
  height: number;
}

// Pick the entity under a screen-space point.
//
// Two-pass strategy:
//   1. Stroke pass: check every visible entity's stroke top-down. If the click
//      is near ANY entity's stroke, that entity wins — a user targeting a line
//      always beats a fill.
//   2. Fill pass: gather every polygon that contains the click and return the
//      one with the smallest area. This handles the "small circle drawn inside
//      a big rectangle" case: without it, the rectangle's fill test fires
//      first and swallows every click, even those clearly aimed at the inner
//      shape.
export function entityAtScreenPos(
  input: HitTestInput,
  sx: number,
  sy: number,
  pxThreshold: number = 5
): Entity | null {
  const world = screenToWorld(sx, sy, input.zoom, input.pan, input.height);
  // Screen-pixel tolerance converted to world units at the current zoom. The
  // default 5px suits mouse precision; callers pass a larger value for touch
  // where the effective aim precision is a fingertip's contact area (~14 px).
  const threshold = pxThreshold / input.zoom;

  const byId = new Map<number, Entity>();
  for (const e of input.entities) byId.set(e.id, e);

  // Topmost first (last layer painted is on top). Locked layers are drawn
  // on the canvas but explicitly excluded from hit-testing so their entities
  // can't be selected.
  const ordered: Entity[] = [];
  for (let i = input.layerOrder.length - 1; i >= 0; i--) {
    const layer = input.layers[input.layerOrder[i]!];
    if (!layer || !layer.visible || layer.locked) continue;
    for (const id of layer.entityIds) {
      const e = byId.get(id);
      if (e) ordered.push(e);
    }
  }

  // ---- Pass 1: stroke hits win ----
  for (const entity of ordered) {
    if (entity.points.length < 2) continue;
    for (let j = 0; j < entity.points.length - 1; j++) {
      const p1 = entity.points[j]!;
      const p2 = entity.points[j + 1]!;
      if (distanceToSegment(world.x, world.y, p1[0], p1[1], p2[0], p2[1]) < threshold) {
        return entity;
      }
    }
  }

  // ---- Pass 2: smallest containing polygon wins ----
  let best: Entity | null = null;
  let bestArea = Infinity;
  for (const entity of ordered) {
    if (
      entity.type !== "LWPOLYLINE" &&
      entity.type !== "POLYLINE" &&
      entity.type !== "CIRCLE"
    ) {
      continue;
    }
    if (entity.points.length < 3) continue;
    if (!pointInPolygon(world.x, world.y, entity.points)) continue;
    const area = Math.abs(polygonSignedArea(entity.points));
    if (area < bestArea) {
      bestArea = area;
      best = entity;
    }
  }
  return best;
}

// Shoelace-formula signed area of a polygon (positive = CCW, negative = CW).
function polygonSignedArea(polygon: ReadonlyArray<readonly [number, number]>): number {
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    sum += (xj + xi) * (yj - yi);
  }
  return sum / 2;
}

// Which entities fall inside / cross / outside a screen-space rectangle,
// under the given selection mode.
//
// - "inside"  = every vertex is inside the box (window selection).
// - "cross"   = any vertex inside the box, OR any segment intersects a box
//               edge. The segment test is what lets a right-to-left drag
//               inside a big shape catch that shape via its outline, even
//               when none of the shape's vertices are inside the tiny box.
// - "outside" = every vertex is outside AND no segment crosses a box edge
//               (i.e. the entity is fully disjoint from the box's outline).
export function entitiesInBox(
  input: HitTestInput,
  sx1: number,
  sy1: number,
  sx2: number,
  sy2: number,
  mode: SelectionMode
): Entity[] {
  const c1 = screenToWorld(sx1, sy1, input.zoom, input.pan, input.height);
  const c2 = screenToWorld(sx2, sy2, input.zoom, input.pan, input.height);
  const minX = Math.min(c1.x, c2.x);
  const maxX = Math.max(c1.x, c2.x);
  const minY = Math.min(c1.y, c2.y);
  const maxY = Math.max(c1.y, c2.y);

  // The four box edges as segments, for the segment-intersection pass.
  const boxEdges: ReadonlyArray<readonly [number, number, number, number]> = [
    [minX, minY, maxX, minY], // bottom
    [maxX, minY, maxX, maxY], // right
    [maxX, maxY, minX, maxY], // top
    [minX, maxY, minX, minY], // left
  ];

  const results: Entity[] = [];

  for (const entity of input.entities) {
    const layer = input.layers[entity.layer];
    if (!layer || !layer.visible || layer.locked) continue;
    const pts = entity.points;
    if (pts.length === 0) continue;

    let allInside = true;
    let anyInside = false;
    for (const [x, y] of pts) {
      const inBox = x >= minX && x <= maxX && y >= minY && y <= maxY;
      if (inBox) anyInside = true;
      else allInside = false;
    }

    if (mode === "inside") {
      if (allInside) results.push(entity);
      continue;
    }

    // Only run the segment-crossing test when it can change the answer:
    //  - cross:   already true if anyInside, so skip
    //  - outside: only relevant if no vertex is inside (otherwise excluded)
    let crosses = false;
    if ((mode === "cross" && !anyInside) || (mode === "outside" && !anyInside)) {
      const isClosed =
        entity.type === "LWPOLYLINE" ||
        entity.type === "POLYLINE" ||
        entity.type === "CIRCLE";
      const n = pts.length;
      const segCount = isClosed && n >= 3 ? n : n - 1;
      outer: for (let i = 0; i < segCount; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[(i + 1) % n]!;
        for (const [ex1, ey1, ex2, ey2] of boxEdges) {
          if (segmentsIntersect(ax, ay, bx, by, ex1, ey1, ex2, ey2)) {
            crosses = true;
            break outer;
          }
        }
      }
    }

    if (mode === "cross" && (anyInside || crosses)) results.push(entity);
    else if (mode === "outside" && !anyInside && !crosses) results.push(entity);
  }

  return results;
}
